import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, Star, QrCode, Type, Gift, ShoppingBag, Image, Layout } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { API } from "@/lib/api";
import { DEFAULT_BRAND_COLOR, DEFAULT_ON_BRAND, brand } from "@/lib/brand";


const CARD_ELEMENTS = {
  logo_header: { label: "Logo Header", icon: Image, description: "Wide brand logo at top" },
  business_info: { label: "Business Name & Icon", icon: Type, description: "Name + small logo icon" },
  loyalty_status: { label: "Loyalty Status", icon: Star, description: "Points balance or stamp card" },
  qr_code: { label: "QR Code", icon: QrCode, description: "Staff scan to view/redeem" },
  reward_info: { label: "Reward Description", icon: Gift, description: "Current reward details" },
  milestones: { label: "Milestones / Tiers", icon: ShoppingBag, description: "Progress milestones" },
};

const DEFAULT_LAYOUT = [
  { id: "logo_header", visible: true },
  { id: "business_info", visible: true },
  { id: "loyalty_status", visible: true },
  { id: "qr_code", visible: true },
  { id: "reward_info", visible: true },
  { id: "milestones", visible: true },
];

function SortableItem({ item, onToggle }) {
  const meta = CARD_ELEMENTS[item.id];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  const Icon = meta?.icon || Layout;

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${isDragging ? "shadow-lg bg-white border-purple-300 ring-2 ring-purple-100" : item.visible ? "bg-white border-ink/10" : "bg-surface border-line"}`}
      data-testid={`card-element-${item.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-white/70 hover:text-ink/50 touch-none" data-testid={`drag-handle-${item.id}`}>
        <GripVertical size={16} />
      </button>
      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${item.visible ? "bg-purple-50 text-purple-600" : "bg-purple-50 text-ink/40"}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${item.visible ? "text-ink" : "text-ink/40"}`}>{meta?.label || item.id}</p>
        <p className="text-[10px] text-ink/40 truncate">{meta?.description}</p>
      </div>
      <Switch checked={item.visible} onCheckedChange={() => onToggle(item.id)} data-testid={`toggle-${item.id}`} />
    </div>
  );
}

function CardPreview({ layout, brandColor, textColor, vendorId, businessName, rewardLogic, vendor }) {
  const bg = brandColor || DEFAULT_BRAND_COLOR;
  const fg = textColor || DEFAULT_ON_BRAND;
  const visibleItems = layout.filter(i => i.visible);

  const renderElement = (item) => {
    switch (item.id) {
      case "logo_header":
        return vendor?.logo_header_path ? (
          <div className="px-4 py-2 flex justify-center" key={item.id}>
            <img src={`${API}/vendors/${vendorId}/logo/header`} alt="" className="h-8 max-w-[140px] object-contain"
              style={{ filter: "brightness(0) invert(1)", opacity: 0.9 }} />
          </div>
        ) : (
          <div className="px-4 py-2 flex justify-center" key={item.id}>
            <div className="h-7 w-28 rounded border border-dashed flex items-center justify-center text-[9px]"
              style={{ borderColor: fg + "44", color: fg + "66" }}>LOGO</div>
          </div>
        );

      case "business_info":
        return (
          <div className="px-4 py-2 flex items-center gap-2" key={item.id}>
            {vendor?.logo_icon_path ? (
              <img src={`${API}/vendors/${vendorId}/logo/icon`} alt="" className="w-6 h-6 rounded-md object-cover" />
            ) : (
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: fg + "22" }}>
                <Star size={10} style={{ color: fg }} />
              </div>
            )}
            <div>
              <p className="text-xs font-bold leading-tight" style={{ color: fg }}>{businessName || "Business"}</p>
              <p className="text-[8px] leading-tight" style={{ color: fg + "88" }}>Rewards</p>
            </div>
          </div>
        );

      case "loyalty_status":
        if (rewardLogic === "order_based") {
          return (
            <div className="px-4 py-2" key={item.id}>
              <div className="flex gap-1 justify-center">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="w-5 h-5 rounded-full border"
                    style={{ borderColor: fg + "55", backgroundColor: i < 2 ? fg : "transparent" }} />
                ))}
              </div>
              <p className="text-center text-[8px] mt-1" style={{ color: fg + "88" }}>2 / 6 stamps</p>
            </div>
          );
        }
        return (
          <div className="px-4 py-2 text-center" key={item.id}>
            <p className="text-[8px] uppercase tracking-wider" style={{ color: fg + "88" }}>Balance</p>
            <p className="text-lg font-bold font-mono" style={{ color: fg }}>350</p>
            <p className="text-[8px]" style={{ color: fg + "66" }}>points</p>
          </div>
        );

      case "qr_code":
        return (
          <div className="px-4 py-2 flex flex-col items-center" key={item.id}>
            <div className="w-14 h-14 rounded-md border flex items-center justify-center"
              style={{ borderColor: fg + "33", backgroundColor: fg + "11" }}>
              <QrCode size={28} style={{ color: fg + "88" }} />
            </div>
            <p className="text-[7px] mt-1" style={{ color: fg + "66" }}>scan here</p>
          </div>
        );

      case "reward_info":
        return (
          <div className="px-4 py-1.5" key={item.id}>
            <p className="text-[8px] uppercase tracking-wider" style={{ color: fg + "88" }}>Reward</p>
            <p className="text-[10px] font-medium" style={{ color: fg }}>
              {rewardLogic === "order_based" ? "Free Item at 5 stamps" : "£5 off at 500 pts"}
            </p>
          </div>
        );

      case "milestones":
        if (rewardLogic === "order_based") {
          return (
            <div className="px-4 py-1.5 space-y-0.5" key={item.id}>
              <p className="text-[8px] uppercase tracking-wider" style={{ color: fg + "88" }}>Milestones</p>
              {[{ n: 5, r: "Free Item" }, { n: 10, r: "£5 cash" }].map((m, i) => (
                <div key={i} className="flex items-center justify-between text-[9px]" style={{ color: fg + "cc" }}>
                  <span>{m.n} stamps</span><span>{m.r}</span>
                </div>
              ))}
            </div>
          );
        }
        return (
          <div className="px-4 py-1.5 space-y-0.5" key={item.id}>
            <p className="text-[8px] uppercase tracking-wider" style={{ color: fg + "88" }}>Progress</p>
            <div className="w-full rounded-full h-1.5" style={{ backgroundColor: fg + "22" }}>
              <div className="rounded-full h-1.5" style={{ width: "60%", backgroundColor: fg }} />
            </div>
            <p className="text-[8px] text-right" style={{ color: fg + "66" }}>150 more to go</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center" data-testid="card-designer-preview">
      {/* Phone frame */}
      <div className="relative w-[200px]">
        <div className="rounded-[20px] border-[3px] border-ink overflow-hidden shadow-xl bg-ink">
          {/* Notch */}
          <div className="flex justify-center pt-1.5 pb-1 bg-ink">
            <div className="w-16 h-1.5 rounded-full bg-ink-soft" />
          </div>
          {/* Card */}
          <div className="mx-2 mb-2 rounded-xl overflow-hidden" style={{ backgroundColor: bg }}>
            <div className="py-2 space-y-0">
              {visibleItems.map((item) => renderElement(item))}
            </div>
          </div>
          {/* Bottom bar */}
          <div className="flex justify-center pb-1.5 bg-ink">
            <div className="w-8 h-1 rounded-full bg-ink-soft" />
          </div>
        </div>
      </div>
      <p className="text-[10px] text-ink/40 mt-2 uppercase tracking-widest font-medium">Live Preview</p>
    </div>
  );
}

export default function CardDesigner({ vendor, vendorId, brandColor, textColor, onLayoutChange }) {
  const [layout, setLayout] = useState(() => {
    if (vendor?.card_layout?.length > 0) return vendor.card_layout;
    return DEFAULT_LAYOUT.map(i => ({ ...i }));
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = layout.findIndex(i => i.id === active.id);
    const newIndex = layout.findIndex(i => i.id === over.id);
    const updated = arrayMove(layout, oldIndex, newIndex);
    setLayout(updated);
    onLayoutChange?.(updated);
  };

  const handleToggle = (id) => {
    const updated = layout.map(i => i.id === id ? { ...i, visible: !i.visible } : i);
    setLayout(updated);
    onLayoutChange?.(updated);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="card-designer">
      {/* Left: Sortable element list */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <Layout size={14} className="text-ink/50" />
          <p className="text-sm font-semibold text-ink">Card Elements</p>
        </div>
        <p className="text-xs text-ink/40 mb-2">Drag to reorder. Toggle visibility on/off.</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={layout.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {layout.map((item) => (
                <SortableItem key={item.id} item={item} onToggle={handleToggle} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Right: Phone preview */}
      <div className="flex justify-center pt-2">
        <CardPreview
          layout={layout}
          brandColor={brandColor}
          textColor={textColor}
          vendorId={vendorId}
          businessName={vendor?.name}
          rewardLogic={vendor?.reward_logic}
          vendor={vendor}
        />
      </div>
    </div>
  );
}

export { DEFAULT_LAYOUT, CARD_ELEMENTS };
